// ORCA360 - Transcription Hook
// Sprint 2, Step 1: Frontend integration for Whisper ASR
// FULL PIPELINE ENABLED: Transcription → Diarization → Events (2025-10-21)

import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

interface TranscriptionStatus {
  status: 'idle' | 'processing' | 'completed' | 'error'; // 'queued' removed - Inngest handles all processing now
  progress: number;
  error?: string;
  transcriptId?: string;
  wordCount?: number;
  // queueJobId removed (Feb 2026) - legacy transcription_queue no longer used
}

interface ProcessingJob {
  id: string;
  job_type: string;
  status: string;
  progress: number;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}

export const useTranscription = (sessionId?: string) => {
  const [transcriptionStatus, setTranscriptionStatus] = useState<TranscriptionStatus>({
    status: 'idle',
    progress: 0,
  });
  const [currentJob, setCurrentJob] = useState<ProcessingJob | null>(null);

  // Refs for cleanup: pollIntervalRef allows the useEffect cleanup to cancel an active
  // polling interval even when startPolling() is called outside the effect system.
  // mountedRef guards against stale state updates after navigation (Session A → Session B).
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);

  // Poll for existing transcription jobs when component mounts
  useEffect(() => {
    if (!sessionId) {
      console.log('[Transcribe] No sessionId, skipping subscription setup');
      return;
    }

    // Reset mounted state for this effect run (handles session navigation re-mounts)
    mountedRef.current = true;

    console.log('[Transcribe] Setting up real-time subscription for session:', sessionId);
    checkExistingTranscription(sessionId);
    
    // Set up real-time subscription for job updates
    let realtimeErrorCount = 0;
    const MAX_REALTIME_ERRORS = 3;

    const channel = supabase
      .channel(`transcription-${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'processing_jobs',
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          console.log('[Transcribe] Real-time update received:', payload);
          
          if (payload.new) {
            const job = payload.new as any;
            
            if (job.job_type === 'transcription') {
              console.log('[Transcribe] Real-time - Status:', job.status, 'Progress:', job.progress);
              
              if (job.status === 'completed') {
                console.log('[Transcribe] Real-time - Job completed!');
                checkExistingTranscription(sessionId);
              } else if (job.status === 'failed') {
                console.error('[Transcribe] ❌ TRANSCRIPTION FAILED (real-time)');
                console.error('[Transcribe] Error:', job.error_message);
                console.error('[Transcribe] Session ID:', sessionId);

                if (job.error_message?.includes('AssemblyAI')) {
                  console.error('[Transcribe] 🔴 AssemblyAI API Error detected');
                }
                if (job.error_message?.includes('speech_models')) {
                  console.error('[Transcribe] 🔴 speech_models parameter issue - check API version');
                }

                setTranscriptionStatus({
                  status: 'error',
                  progress: job.progress || 0,
                  error: job.error_message || 'Transcription failed',
                });
              } else {
                console.log('[Transcribe] Real-time - Updating progress to:', job.progress);
                setTranscriptionStatus((prev) => ({
                  ...prev,
                  status: 'processing',
                  progress: job.progress || prev.progress,
                }));
              }
            }
          }
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          realtimeErrorCount++;
          if (realtimeErrorCount <= 1) {
            console.warn('[Transcribe] Realtime WebSocket connection failed. Falling back to polling for updates.');
          }
          if (realtimeErrorCount >= MAX_REALTIME_ERRORS) {
            console.warn('[Transcribe] Realtime unavailable after', MAX_REALTIME_ERRORS, 'attempts — unsubscribing. Polling will handle updates.');
            supabase.removeChannel(channel);
          }
        } else {
          console.log('[Transcribe] Subscription status:', status);
        }
      });
    
    // Cleanup subscription and any active polling interval
    return () => {
      console.log('[Transcribe] Cleaning up real-time subscription and polling');
      mountedRef.current = false;
      supabase.removeChannel(channel);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [sessionId]);

  /**
   * Check if transcription already exists or is in progress
   */
  const checkExistingTranscription = async (sessionId: string) => {
    try {
      // Check for existing transcript
      const { data: transcript, error: transcriptError } = await supabase
        .from('transcripts')
        .select('id, word_count, processing_status')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (transcriptError) {
        // Ignore 406 errors - schema cache needs refresh
        if (transcriptError.code === '406' || transcriptError.message?.includes('406')) {
          console.log('[Transcribe] Schema not ready (406), will retry');
          return;
        }
        if (transcriptError.code === 'PGRST116') {
          console.log('[Transcribe] No transcript found - this is normal');
        } else {
          console.warn('[Transcribe] Transcript query error:', transcriptError);
        }
      } else if (transcript) {
        // Only mark as completed if processing_status is 'completed'
        if (transcript.processing_status === 'completed') {
          if (mountedRef.current) {
            setTranscriptionStatus({
              status: 'completed',
              progress: 100,
              transcriptId: transcript.id,
              wordCount: transcript.word_count || 0,
            });
          }
          return;
        } else {
          // Transcript exists but is still processing - check for active job
          console.log(`[Transcribe] Transcript exists but processing_status is '${transcript.processing_status}', checking for active job...`);
        }
      }

      // NOTE: Legacy queue-status check removed (Feb 2026)
      // The old transcription_queue-based system has been replaced by Inngest event-driven architecture
      // Status is now tracked via inngest_job_status table with real-time subscription

      // Check for in-progress processing job
      const { data: job, error: jobError } = await supabase
        .from('processing_jobs')
        .select('*')
        .eq('session_id', sessionId)
        .eq('job_type', 'transcription')
        .in('status', ['pending', 'processing'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (jobError) {
        // Ignore 406 errors - schema cache needs refresh
        if (jobError.code === '406' || jobError.message?.includes('406')) {
          console.log('[Transcribe] Schema not ready (406), will retry');
          return;
        }
        if (jobError.code === 'PGRST116') {
          console.log('[Transcribe] No processing job found - this is normal');
        } else {
          console.warn('[Transcribe] Job query error:', jobError);
        }
      } else if (job) {
        if (mountedRef.current) {
          setCurrentJob(job);
          setTranscriptionStatus({
            status: 'processing',
            progress: job.progress || 0,
          });
        }
        startPolling(sessionId);
      } else if (transcript && transcript.processing_status === 'processing') {
        // Transcript exists with processing_status='processing' but no active job found
        // This might be a stale state - start polling to check for updates
        console.log('[Transcribe] Transcript is processing but no active job found - starting polling to check status');
        if (mountedRef.current) {
          setTranscriptionStatus({
            status: 'processing',
            progress: 0, // Unknown progress
          });
        }
        startPolling(sessionId);
      }
    } catch (error) {
      // Handle 406 errors gracefully
      console.log('[Transcribe] Schema not ready yet - PostgREST needs refresh');
    }
  };

  /**
   * Start transcription process
   */
  const startTranscription = async (
    sessionId: string,
    audioFilePath: string,
    fileId: string,
    options?: { triggerStitch?: boolean; phase?: string }
  ) => {
    try {
      console.log('[Transcribe] 🚀 Starting transcription for session:', sessionId);
      if (options?.triggerStitch) {
        console.log('[Transcribe] 🧵 Stitch mode enabled — will stitch with existing transcript after transcription');
      }
      // Optimistic update: Show 10% immediately (downloading/initializing)
      setTranscriptionStatus({ status: 'processing', progress: 10 });

      // Call transcribe-v2 directly (not process-session-v2)
      // This allows us to handle queue vs direct processing
      const response = await fetch('/api/transcribe-v2', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          audioFilePath,
          fileId,
          ...(options?.triggerStitch ? { triggerStitch: true } : {}),
          ...(options?.phase ? { phase: options.phase } : {}),
        }),
      });

      // Handle non-OK responses before trying to parse JSON
      // Server sometimes returns plain text errors (e.g., "A server error occurred")
      if (!response.ok) {
        let errorMessage = 'Transcription request failed';
        const contentType = response.headers.get('content-type');

        try {
          if (contentType?.includes('application/json')) {
            const errorResult = await response.json();
            errorMessage = errorResult.error || errorResult.message || errorMessage;
            console.error('[Transcribe] API error (JSON):', errorResult);
          } else {
            const errorText = await response.text();
            errorMessage = errorText || `Server error (${response.status})`;
            console.error('[Transcribe] API error (text):', errorText);
          }
        } catch (parseError) {
          console.error('[Transcribe] Could not parse error response:', parseError);
          errorMessage = `Server error (${response.status})`;
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();

      console.log('[Transcribe] ✅ Request accepted, API responded:', result);

      // NOTE: Legacy queue-based handling removed (Feb 2026)
      // All transcription now goes through Inngest event-driven pipeline
      // Real-time updates come via Supabase subscription to inngest_job_status
      console.log('[Transcribe] ⚡ Processing via Inngest pipeline');
      setTranscriptionStatus(prev => ({
        ...prev,
        status: 'processing',
        progress: 15,
        transcriptId: result.transcriptId,
      }));
      startPolling(sessionId);

    } catch (error) {
      console.error('[Transcribe] Error starting transcription:', error);
      setTranscriptionStatus({
        status: 'error',
        progress: 0,
        error: (error as Error).message,
      });
    }
  };

  // NOTE: Legacy startQueuePolling function removed (Feb 2026)
  // The old transcription_queue-based polling has been replaced by Inngest event-driven architecture
  // Real-time updates now come via Supabase subscription to inngest_job_status table

  /**
   * Poll for job status updates (for direct processing or after queue completion)
   * Uses adaptive polling: fast initially (200ms), then slower (1s) after 20 seconds
   * Stops if no job is found after 15 consecutive attempts
   */
  const startPolling = (sessionId: string) => {
    console.log('[Transcribe] 🔄 Starting ADAPTIVE polling for session:', sessionId);

    let pollCount = 0;
    let noJobFoundCount = 0; // Track consecutive "no job found" responses
    // 60 attempts: ~12s at 200ms + up to ~45s at 1s after interval switch.
    // Inngest may take 5–10s to write the processing_jobs row after being triggered.
    const MAX_NO_JOB_ATTEMPTS = 60;
    // Hard wall-clock cap for a job that IS found but never leaves 'processing'.
    // MAX_NO_JOB_ATTEMPTS only covers the no-row case, so a job wedged server-side
    // used to poll indefinitely (val session 4353a1f6-…, 2026-08-18: 800s+ and counting).
    // Sized above the pipeline's own ceiling — it polls AssemblyAI for up to 30 min
    // without touching processing_jobs — so a legitimately slow run is never cut off.
    const MAX_POLL_SECONDS = 45 * 60;
    const startTime = Date.now();
    let currentInterval = 200; // Start with 200ms for fast updates
    let pollInterval: NodeJS.Timeout; // Declare interval variable

    const poll = async () => {
      // Guard: stop immediately if the component has been unmounted (navigation away)
      if (!mountedRef.current) {
        clearInterval(pollInterval);
        pollIntervalRef.current = null;
        return;
      }

      pollCount++;
      const elapsedSeconds = (Date.now() - startTime) / 1000;
      console.log(`[Transcribe] 🔄 Poll #${pollCount} (${elapsedSeconds.toFixed(1)}s elapsed) - Checking job status...`);

      if (elapsedSeconds > MAX_POLL_SECONDS) {
        console.error(
          `[Transcribe] 🛑 Giving up after ${(elapsedSeconds / 60).toFixed(1)} min - job never left 'processing'`
        );
        clearInterval(pollInterval);
        pollIntervalRef.current = null;
        if (mountedRef.current) {
          setTranscriptionStatus(prev => ({
            ...prev,
            status: 'error',
            error:
              'Transcription is taking longer than expected and may have stalled. ' +
              'It might still finish in the background — reload the page to check, ' +
              'or start it again.',
          }));
        }
        return;
      }

      try {
        // Get latest job
        const { data: job, error: jobError } = await supabase
          .from('processing_jobs')
          .select('*')
          .eq('session_id', sessionId)
          .eq('job_type', 'transcription')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (jobError) {
          // Ignore 406 errors - schema cache needs refresh
          if (jobError.code === '406' || jobError.message?.includes('406')) {
            console.log('[Transcribe] Schema not ready (406), will retry');
            return;
          }
          console.error('[Transcribe] Polling error:', jobError.code, jobError.message);
          return;
        }

        if (job) {
          // Reset no-job counter when we find a job
          noJobFoundCount = 0;

          console.log(`[Transcribe] 📊 Poll #${pollCount} - Job found:`, {
            status: job.status,
            progress: job.progress,
            job_type: job.job_type,
            id: job.id,
          });

          if (mountedRef.current) {
            setCurrentJob(job);
            // Update status with job progress
            setTranscriptionStatus(prev => ({
              ...prev,
              progress: Math.max(prev.progress || 0, job.progress || 0),
            }));
          }

          // Update status
          if (job.status === 'completed') {
            console.log('[Transcribe] ✅ Job completed! Fetching transcript...');

            // Get transcript details (including processing_status)
            const { data: transcript, error: transcriptError } = await supabase
              .from('transcripts')
              .select('id, word_count, processing_status')
              .eq('session_id', sessionId)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();

            if (transcriptError || !transcript) {
              console.log('[Transcribe] ⏳ Transcript not ready yet:', transcriptError);
              // Don't stop polling yet, transcript might still be creating
              return;
            }

            // Only mark as completed if transcript processing_status is also 'completed'
            if (transcript.processing_status === 'completed') {
              console.log('[Transcribe] ✅ Transcript found and completed:', transcript.id);

              if (mountedRef.current) {
                setTranscriptionStatus({
                  status: 'completed',
                  progress: 100,
                  transcriptId: transcript.id,
                  wordCount: transcript.word_count || 0,
                });
              }
            } else {
              console.log(`[Transcribe] ⏳ Job completed but transcript processing_status is '${transcript.processing_status}' - continuing to poll`);
              // Continue polling - transcript might still be processing
              return;
            }

            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            console.log('[Transcribe] ✅ Polling stopped - job complete');
          } else if (job.status === 'failed') {
            // Enhanced error logging for debugging transcription failures
            console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
            console.error('[Transcribe] ❌ TRANSCRIPTION FAILED');
            console.error('[Transcribe] Error:', job.error_message);
            console.error('[Transcribe] Job ID:', job.id);
            console.error('[Transcribe] Session ID:', sessionId);
            console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

            // Log specific API errors for easier debugging
            if (job.error_message?.includes('AssemblyAI')) {
              console.error('[Transcribe] 🔴 AssemblyAI API Error detected');
              console.error('[Transcribe] Check: API key, speech_models param, language_code');
            }
            if (job.error_message?.includes('400')) {
              console.error('[Transcribe] 🔴 HTTP 400 Bad Request - Check API parameters');
            }
            if (job.error_message?.includes('401') || job.error_message?.includes('403')) {
              console.error('[Transcribe] 🔴 Authentication Error - Check ASSEMBLYAI_API_KEY');
            }

            if (mountedRef.current) {
              setTranscriptionStatus({
                status: 'error',
                progress: job.progress || 0,
                error: job.error_message || 'Transcription failed',
              });
            }

            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            console.log('[Transcribe] ❌ Polling stopped - job failed');
          } else {
            // Still processing - update progress from database
            const newProgress = job.progress || 0;
            console.log(`[Transcribe] 🔄 Still processing... Progress: ${newProgress}%`);
          }
        } else {
          noJobFoundCount++;
          console.warn(`[Transcribe] ⚠️  Poll #${pollCount} - No job found (${noJobFoundCount}/${MAX_NO_JOB_ATTEMPTS})`);

          // Stop polling if no job found after multiple attempts
          if (noJobFoundCount >= MAX_NO_JOB_ATTEMPTS) {
            console.log('[Transcribe] 🛑 Stopping polling - no job found after', MAX_NO_JOB_ATTEMPTS, 'attempts');
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            if (mountedRef.current) {
              setTranscriptionStatus(prev => ({
                ...prev,
                status: 'idle',
                progress: 0,
              }));
            }
            return;
          }
        }

        // Adaptive polling: switch to slower polling after 20 seconds
        if (elapsedSeconds > 20 && currentInterval === 200) {
          clearInterval(pollInterval);
          currentInterval = 1000; // Slow down to 1 second
          console.log('[Transcribe] 📊 Switching to slower polling (1s interval)');
          pollInterval = setInterval(poll, currentInterval);
          pollIntervalRef.current = pollInterval; // Keep ref in sync after interval replacement
        }

      } catch (error) {
        console.error('[Transcribe] Polling exception:', error);
      }
    };

    // Start with fast polling (200ms)
    pollInterval = setInterval(poll, currentInterval);
    pollIntervalRef.current = pollInterval; // Track in ref so useEffect cleanup can cancel it
    console.log(`[Transcribe] ⚡ Started fast polling (${currentInterval}ms interval)`);

    // Cleanup after 30 minutes max (long audio files can take 20+ minutes on AssemblyAI)
    setTimeout(() => {
      clearInterval(pollInterval);
      pollIntervalRef.current = null;
      console.log('[Transcribe] ⏰ Polling timeout reached (30 minutes)');
      if (mountedRef.current) {
        setTranscriptionStatus(prev => {
          // Only set timeout error if still processing (don't overwrite completed/error)
          if (prev.status === 'processing') {
            return {
              ...prev,
              status: 'error',
              error: 'Transcription is still processing on the server. Please refresh the page to check the latest status.',
            };
          }
          return prev;
        });
      }
    }, 1800000);
  };

  /**
   * Reset transcription state
   */
  const resetTranscription = () => {
    setTranscriptionStatus({ status: 'idle', progress: 0 });
    setCurrentJob(null);
  };

  return {
    transcriptionStatus,
    currentJob,
    startTranscription,
    resetTranscription,
  };
};

